<template>
  <fieldset v-if="tagsList.length > 0">
    <legend id="ai-tags-title" class="body-1-bold mb-12">
      <span class="flex shrink-0 items-center justify-end gap-4">
        {{ i18n(translations.title) }}

        <RevTooltip>
          <template #trigger="{ show, hide }">
            <IconInfo
              class="inline"
              size="16"
              @focusin="show"
              @focusout="hide"
              @mouseleave="hide"
              @mouseover="show"
            />
          </template>

          {{ tooltipInfo }}
        </RevTooltip>
      </span>
    </legend>

    <div class="flex flex-wrap gap-8">
      <ul id="aiTagsList" class="contents">
        <li
          v-for="(tag, index) in tagsList"
          :key="tag.id"
          :class="{ hidden: index >= MAX_TAGS && isTagsListCollapsed }"
        >
          <Chip
            v-model="selectedTags"
            :count="tag.count"
            :icon="tag.icon"
            :label="tag.label"
            :value="tag.id"
            :variant="tag.sentiment"
            @change="trackChange(tag)"
            @update:model-value="updateSelectedTags"
          />
        </li>
      </ul>

      <RevChip
        v-if="canDisplayMoreTags"
        v-model="isToggleButtonActive"
        aria-describedby="ai-tags-title"
        :aria-expanded="!isTagsListCollapsed"
        :endIcon="isTagsListCollapsed ? IconChevronDown : IconChevronUp"
        :label="
          isTagsListCollapsed
            ? i18n(translations.showMore, {
                count: selectedTagsInCollapsedList.length,
              })
            : i18n(translations.showLess)
        "
        @click="toggleTagsList"
      />
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { type Component, computed, ref, watch } from 'vue'

import type { ReviewsTagDetails } from '@backmarket/http-api/src/api-specs-reviews/types/ai-summary'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevChip } from '@ds/components/Chip'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'
import { IconInfo } from '@ds/icons/IconInfo'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import { TRACKING_ZONE } from '~/scopes/reviews/reviews-display/constants'

import { AI_SUMMARY_REVIEW_COUNT_THRESHOLD } from '../../aiSummary.constants'

import translations from './AITags.translations'
import Chip from './Chip.vue'
import { formatRawTag, sentimentToIcon, tagToLabel } from './helpers'

const { tags, updatedAt, reviewsCount } = defineProps<{
  tags: ReviewsTagDetails[]
  updatedAt?: string | null
  reviewsCount?: number | null
}>()

const MAX_TAGS = 6
const canDisplayMoreTags = tags.length > MAX_TAGS

const i18n = useI18n()
const route = useRoute()
const { trackClick } = useTracking()

type TagsListItem = {
  id: ReviewsTagDetails['tag']
  label: string
  count: ReviewsTagDetails['mentionCount']
  icon: Component
  sentiment: ReviewsTagDetails['sentiment']
}

const tagsList = computed<TagsListItem[]>(() => {
  return tags.map(({ tag, mentionCount, sentiment }) => ({
    id: tag,
    label: i18n(tagToLabel[tag]) ?? formatRawTag(tag),
    count: mentionCount,
    icon: sentimentToIcon[sentiment],
    sentiment,
  }))
})

const isTagsListCollapsed = ref(canDisplayMoreTags)

const selectedTags = ref<string[]>([])
const selectedTagsInCollapsedList = ref<string[]>([])
const isToggleButtonActive = computed(
  () =>
    isTagsListCollapsed.value && selectedTagsInCollapsedList.value.length > 0,
)

const formattedUpdatedAt = computed(() => {
  if (!updatedAt) return ''

  return i18n.date(new Date(updatedAt))
})

const tooltipInfo = computed(() => {
  if (!reviewsCount || reviewsCount < AI_SUMMARY_REVIEW_COUNT_THRESHOLD) {
    return i18n(translations.tooltipInfoAllReviews, {
      updateDate: formattedUpdatedAt.value,
    })
  }

  return i18n(translations.tooltipInfoLatestReviews, {
    number: AI_SUMMARY_REVIEW_COUNT_THRESHOLD,
    updateDate: formattedUpdatedAt.value,
  })
})

async function updateSelectedTags() {
  await navigateTo({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      tags__in: selectedTags.value.length > 0 ? selectedTags.value : undefined,
      cursor: undefined,
    },
  })
}

function trackChange(tag: TagsListItem) {
  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[route.name?.toString() || ''],
    zone: TRACKING_ZONE,
    name: `review_tags_filter_${tag.id.toLowerCase()}`,
    sentiment: tag.sentiment,
    count: tag.count,
    action: selectedTags.value.includes(tag.id) ? 'unselect' : 'select',
  })
}

watch(
  () => route.query,
  () => {
    const { tags__in: tagsFromQuery } = route.query

    if (!tagsFromQuery) {
      selectedTags.value = []
    }

    if (typeof tagsFromQuery === 'string') selectedTags.value = [tagsFromQuery]
    if (Array.isArray(tagsFromQuery))
      selectedTags.value = tagsFromQuery.filter(
        (tag): tag is string => typeof tag === 'string' && !!tag,
      )
    selectedTagsInCollapsedList.value = selectedTags.value.filter(
      (tag) => tagsList.value.findIndex((t) => t.id === tag) > MAX_TAGS - 1,
    )
  },
  { immediate: true },
)

function toggleTagsList() {
  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[route.name?.toString() || ''],
    zone: TRACKING_ZONE,
    name: isTagsListCollapsed.value
      ? 'review_tags_filter_show_more'
      : 'review_tags_filter_show_less',
  })

  isTagsListCollapsed.value = !isTagsListCollapsed.value
}
</script>
